/**
 * La media móvil que convierte una serie en una tendencia.
 *
 * Un gráfico de barras responde «cuánto» y ninguno responde «hacia dónde». En
 * el tenant productivo eso esconde cosas: entre el 13 y el 26 de agosto las
 * operaciones subieron de 108 a 187 de media mientras la conversión bajaba de
 * 41,7% a 35,7%. Cada día por separado se ve bien; la tendencia no.
 *
 * Hace falta suavizar porque el fin de semana domina la serie — los sábados
 * caen a 20 contra 300 entre semana— y sin eso no se distingue una caída real
 * de un domingo.
 */

/** Cuántos días entran en el promedio. Siete cubre la semana entera. */
export const VENTANA_POR_DEFECTO = 7;

/**
 * Media móvil simple. Los primeros puntos son `null`, no cero.
 *
 * La distinción importa: un cero dibuja una caída a la base que nunca ocurrió,
 * y Recharts la une con el resto de la línea. `null` deja el tramo sin dibujar,
 * que es lo honesto — todavía no hay suficiente historia para una tendencia.
 */
export function mediaMovil(
  valores: ReadonlyArray<number | null | undefined>,
  ventana: number = VENTANA_POR_DEFECTO,
): Array<number | null> {
  if (ventana < 1) return valores.map(() => null);

  return valores.map((_, i) => {
    if (i < ventana - 1) return null;

    const tramo = valores.slice(i - ventana + 1, i + 1);
    // Un hueco en el tramo invalida el promedio: sumar sobre menos puntos daría
    // un número más bajo que la realidad y la tendencia mentiría hacia abajo.
    if (tramo.some((v) => v === null || v === undefined || !Number.isFinite(v))) return null;

    const suma = (tramo as number[]).reduce((a, b) => a + b, 0);
    return Math.round((suma / ventana) * 10) / 10;
  });
}

/**
 * Hacia dónde va la serie, comparando los extremos de la tendencia.
 *
 * Devuelve `null` cuando no hay dos puntos de tendencia que comparar — con
 * menos historia que la ventana, cualquier flecha sería inventada.
 */
export function direccion(tendencia: ReadonlyArray<number | null>): {
  cambio: number;
  sentido: 'sube' | 'baja' | 'estable';
} | null {
  const puntos = tendencia.filter((v): v is number => v !== null);
  if (puntos.length < 2) return null;

  const cambio = Math.round((puntos[puntos.length - 1] - puntos[0]) * 10) / 10;
  // Un cambio ínfimo no es una tendencia. El umbral evita que la flecha
  // oscile día a día por ruido y termine ignorándose.
  const sentido = Math.abs(cambio) < 0.05 ? 'estable' : cambio > 0 ? 'sube' : 'baja';

  return { cambio, sentido };
}
